import { useReferral } from "@/hooks/useReferral";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Badge } from "@/components/ui/badge";
import { useToast } from "@/hooks/use-toast";
import { Copy, Share2, Gift } from "lucide-react";

export default function Referrals() {
  const { code, referrals, loading } = useReferral();
  const { toast } = useToast();

  const link = code ? `${window.location.origin}/login?ref=${code}` : "";

  const copy = async (text: string) => {
    await navigator.clipboard.writeText(text);
    toast({ title: "Copied!", description: "Share it with friends." });
  };

  const share = async () => {
    if (navigator.share) {
      try { await navigator.share({ title: "Join Bayzo", text: "Find easy parking with Bayzo. Use my code!", url: link }); } catch {}
    } else copy(link);
  };

  if (loading) return <div className="p-8">Loading...</div>;

  return (
    <div className="container mx-auto p-6 max-w-3xl">
      <h1 className="text-3xl font-bold mb-6 flex items-center gap-2"><Gift className="h-7 w-7" />Refer & Earn</h1>
      <Card className="mb-6">
        <CardHeader><CardTitle>Your Referral Code</CardTitle></CardHeader>
        <CardContent className="space-y-4">
          <div className="flex gap-2">
            <Input value={code ?? ""} readOnly className="font-mono text-lg font-bold" />
            <Button onClick={() => copy(code ?? "")} variant="outline"><Copy className="h-4 w-4" /></Button>
          </div>
          <div className="flex gap-2">
            <Input value={link} readOnly className="text-sm" />
            <Button onClick={share}><Share2 className="h-4 w-4 mr-2" />Share</Button>
          </div>
          <p className="text-sm text-muted-foreground">Share your code. Friends who sign up and complete their first booking earn you a reward.</p>
        </CardContent>
      </Card>

      <Card>
        <CardHeader><CardTitle>Your Referrals ({referrals.length})</CardTitle></CardHeader>
        <CardContent>
          {referrals.length === 0 ? (
            <p className="text-muted-foreground text-sm">No referrals yet. Start sharing!</p>
          ) : (
            <div className="space-y-2">
              {referrals.map((r) => (
                <div key={r.id} className="flex justify-between items-center p-3 border rounded">
                  <div className="text-sm">
                    <div className="font-mono">{r.referred_user_id.slice(0, 8)}...</div>
                    <div className="text-xs text-muted-foreground">{new Date(r.created_at).toLocaleDateString()}</div>
                  </div>
                  <Badge variant={r.status === "qualified" ? "default" : "secondary"}>{r.status}</Badge>
                </div>
              ))}
            </div>
          )}
        </CardContent>
      </Card>
    </div>
  );
}
